import { useEffect, useState } from "react";
import { Phone, Mail, Trash2, CheckCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SUGGESTION_STATUS, fmtDateTime } from "@/components/portal/SaranAspirasi";

const FILTERS = [["all", "Semua"], ["baru", "Baru"], ["dibaca", "Dibaca"], ["ditindaklanjuti", "Ditindaklanjuti"]];

export default function InboxPage({ title, subtitle, endpoint, prefix, emptyText }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/${endpoint}/${id}/status`, { status });
      setItems((l) => l.map((s) => (s.id === id ? { ...s, status } : s)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus pesan ini?")) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      setItems((l) => l.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = filter === "all" ? items : items.filter((s) => s.status === filter);
  const unread = items.filter((s) => s.status === "baru").length;

  return (
    <div data-testid={`${prefix}-page`}>
      <PageHeader title={title} subtitle={`${subtitle} · ${unread} belum dibaca`} />
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${filter === k ? "emerald-bg text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-700"}`} data-testid={`${prefix}-filter-${k}`}>
            {l}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid={`${prefix}-empty`}>{emptyText}</p>
      ) : (
        <div className="space-y-3">
          {shown.map((s) => {
            const [label, cls] = SUGGESTION_STATUS[s.status] || [s.status, ""];
            return (
              <div key={s.id} className={`rounded-2xl border bg-white p-5 ${s.status === "baru" ? "border-sky-300" : "border-slate-200"}`} data-testid={`${prefix}-item-${s.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-emerald-950">{s.subject || "Tanpa judul"}</p>
                    <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                      <span className="font-medium text-slate-700">{s.name}</span>
                      {s.phone && <span>· <Phone size={11} className="inline" /> {s.phone}</span>}
                      {s.email && <span>· <Mail size={11} className="inline" /> {s.email}</span>}
                      <span>· {fmtDateTime(s.created_at)}</span>
                    </p>
                  </div>
                  <Badge className={cls} data-testid={`${prefix}-status-${s.id}`}>{label}</Badge>
                </div>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{s.message}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.status === "baru" && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(s.id, "dibaca")} data-testid={`${prefix}-read-${s.id}`}><Eye size={14} /> Tandai Dibaca</Button>
                  )}
                  {s.status !== "ditindaklanjuti" && (
                    <Button size="sm" className="rounded-full emerald-bg hover:bg-[#145c48]" onClick={() => setStatus(s.id, "ditindaklanjuti")} data-testid={`${prefix}-done-${s.id}`}><CheckCheck size={14} /> Ditindaklanjuti</Button>
                  )}
                  {s.phone && (
                    <a href={`https://wa.me/${s.phone}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 h-8 text-xs font-medium text-slate-600 hover:border-emerald-700" data-testid={`${prefix}-wa-${s.id}`}>
                      <Phone size={13} /> Balas via WA
                    </a>
                  )}
                  <Button size="sm" variant="ghost" className="rounded-full text-red-600 hover:bg-red-50 ml-auto" onClick={() => remove(s.id)} data-testid={`${prefix}-delete-${s.id}`}><Trash2 size={14} /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
